import React from "react";
import { useTranslation } from "react-i18next";
import { VehicleListItemDto } from "../../../models/vehicle/vehicle";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { commonBoxStyle, MainLayout } from "../../layout/mainLayout";
import { PageTitle } from "../../layout/pageTitle";
import { Box, TextField } from "@mui/material";
import { CustomerListItemDto } from "../../../models/common/customer";
import MuiSelect from "../../framework/muiSelect";
import { FormikProps, useFormik } from "formik";
import { TransportOrderSaveData } from "../../../models/transport/transportOrderSaveData";
import { defaultFormikConfig, formikFieldProps } from "../../framework/formUtils";
import * as yup from "yup";
import { ClearButton, SubmitButton } from "../../framework/formButtons";
import { handleApiError } from "../../../services/baseService";
import { useAppDispatch, useAppSelector } from "../../../framework/redux/hooks";
import { addSuccessMsg } from "../../../framework/redux/slices/appSlice";
import { EmployeeSessionItemDto } from "../../../models/transport/employee";
import { MuiDateTimePicker } from "../../framework/muiDateTimePicker";
import { Base } from "../../../framework/common/base";
import { addTransportOrder, updateExistingTransportOrder } from "../../../framework/redux/slices/transportOrderDetailsSlice";
import { getGeolocationPosition } from "../../../framework/common/geolocation";
import { AlertSeverity } from "../../../models/common/enums";
import { workTimeGeolocationPositionErrorDuration } from "../../worktime/workTimeTypeDrawer";

interface IOrderFormProps {
    order: TransportOrderSaveData;
    vehicles: VehicleListItemDto[];
    customers: CustomerListItemDto[];
    employees: EmployeeSessionItemDto[];
}

export const OrderForm = ({
    order,
    vehicles,
    customers,
    employees,
}: IOrderFormProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { orderId } = useParams();
    const navigate = useNavigate();

    const usesLocationData = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.usesLocationData
    );

    const handleUpdate = async (formData: TransportOrderSaveData) => {
        if (usesLocationData) {
            getGeolocationPosition({
                message: t("validation.geolocationPositionError"),
                severity: AlertSeverity.Warning,
                duration: workTimeGeolocationPositionErrorDuration,
            })
                .then((location) => {
                    dispatch(updateExistingTransportOrder({
                        orderId: order.id,
                        data: { ...formData, location: location }
                    }))
                        .unwrap()
                        .then(() => {
                            dispatch(
                                addSuccessMsg(t("transport.order.saveSuccessful"))
                            );
                            goBack();
                        })
                        .catch(handleApiError);
                })
                .catch(console.error);
        } else {
            await dispatch(updateExistingTransportOrder({ orderId: order.id, data: formData }))
                .unwrap()
                .then(() => {
                    dispatch(
                        addSuccessMsg(t("transport.order.saveSuccessful"))
                    );
                    goBack();
                })
                .catch(handleApiError);
        }
    };

    const handleSubmit = async (formData: TransportOrderSaveData) => {
        if (usesLocationData) {
            getGeolocationPosition({
                message: t("validation.geolocationPositionError"),
                severity: AlertSeverity.Warning,
                duration: workTimeGeolocationPositionErrorDuration,
            })
                .then((location) => {
                    dispatch(addTransportOrder({ ...formData, location: location }))
                        .unwrap()
                        .then(() => {
                            dispatch(
                                addSuccessMsg(t("transport.order.saveSuccessful"))
                            );
                            goBack();
                        })
                        .catch(handleApiError);
                })
                .catch(console.error);
        } else {
            await dispatch(addTransportOrder(formData))
                .unwrap()
                .then(() => {
                    dispatch(
                        addSuccessMsg(t("transport.order.saveSuccessful"))
                    );
                    goBack();
                })
                .catch(handleApiError);
        }
    };

    const goBack = () => {
        navigate(generatePath("/transport/orders"));
    };

    const checkEndDatetime = (value: string, scheduledStartDateTime: string) => {
        if (
            (!scheduledStartDateTime && !value) ||
            (scheduledStartDateTime && !value)
        )
            return true;

        return Base.isSameOrAfter(value, scheduledStartDateTime);
    };

    const formik: FormikProps<TransportOrderSaveData> = useFormik({
        ...defaultFormikConfig,
        initialValues: order,
        onSubmit: order?.id ? handleUpdate : handleSubmit,
        validationSchema: yup.object({
            billingCustomerId: yup.string().required(
                t("validation.fieldIsRequired", {
                    field: t("transport.order.billingCustomer"),
                })
            ),
            senderCustomerId: yup.string().nullable(),
            receiverCustomerId: yup.string().nullable(),
            vehicleId: yup.string().nullable(),
            employeeId: yup.string().nullable(),
            driverComments: yup.string().nullable(),
            scheduledStartDateTime: yup.string().nullable(),
            scheduledEndDateTime: yup.string()
                .nullable()
                .test(
                    "date-validation",
                    t("validation.endTimeMustBeAfterStartTime"),
                    function (value) {
                        const scheduledStartDateTime: string = this.resolve(
                            yup.ref("scheduledStartDateTime")
                        );
                        return checkEndDatetime(value, scheduledStartDateTime);
                    }
                ),
        }),
    });

    return (
        <MainLayout
            hasPx
            topComponent={
                <PageTitle
                    header={
                        !orderId
                            ? t("transport.orders.header.newOrder")
                            : t("transport.orders.header.editOrder")
                    }
                />
            }
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                }}
            >
                <MuiSelect
                    label={t("transport.order.billingCustomer")}
                    required
                    options={(customers || []).map((i) => ({
                        id: i.id,
                        title: i.name,
                    }))}
                    {...formikFieldProps(formik, "billingCustomerId")}
                />
                <MuiSelect
                    label={t("transport.order.sender")}
                    noSelection={true}
                    options={(customers || []).map((i) => ({
                        id: i.id,
                        title: i.name,
                    }))}
                    {...formikFieldProps(formik, "senderCustomerId")}
                />
                <MuiSelect
                    label={t("transport.order.receiver")}
                    noSelection={true}
                    options={(customers || []).map((i) => ({
                        id: i.id,
                        title: i.name,
                    }))}
                    {...formikFieldProps(formik, "receiverCustomerId")}
                />
                <MuiSelect
                    label={t("common.vehicle")}
                    noSelection={true}
                    options={(vehicles || []).map((i) => ({
                        id: i.id,
                        title: `${i.registerNumber}, ${i.brand}`,
                    }))}
                    {...formikFieldProps(formik, "vehicleId")}
                />
                <MuiSelect
                    label={t("transport.order.driver")}
                    noSelection={true}
                    options={(employees || []).map((i) => ({
                        id: i.id,
                        title: i.fullName,
                    }))}
                    {...formikFieldProps(formik, "employeeId")}
                />
                <Box sx={commonBoxStyle}>
                    <TextField
                        name="comment"
                        label={t("transport.order.comment")}
                        multiline
                        minRows={2}
                        fullWidth
                        {...formikFieldProps(formik, "driverComments")}
                    />
                </Box>
                <Box sx={commonBoxStyle}>
                    <MuiDateTimePicker
                        dateLabel={t("time.startDate")}
                        timeLabel={t("time.startTime")}
                        {...formikFieldProps(formik, "scheduledStartDateTime")}
                        onChange={(value) =>
                            void formik.setFieldValue("scheduledStartDateTime", value)
                        }
                        asUtc={false}
                    />
                </Box>
                <Box sx={commonBoxStyle}>
                    <MuiDateTimePicker
                        dateLabel={t("time.endDate")}
                        timeLabel={t("time.endTime")}
                        {...formikFieldProps(formik, "scheduledEndDateTime")}
                        onChange={(value) => {
                            void formik.setFieldValue("scheduledEndDateTime", value);
                        }}
                        asUtc={false}
                    />
                </Box>
                <Box sx={commonBoxStyle}>
                    <ClearButton formik={formik} />
                    <SubmitButton formik={formik} />
                </Box>
            </Box>
        </MainLayout>
    );
};
